import { ChevronDown, ChevronRight } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { BlueprintLockHistory } from "./BlueprintLockHistory";
import { BlueprintVersionDiff } from "./BlueprintVersionDiff";
import type { BlueprintVersion, LockEvent } from "./blueprintTypes";

interface Props {
  events: LockEvent[];
  versions: BlueprintVersion[];
}

function defaultFromVersionId(versions: BlueprintVersion[]): string | null {
  // Most-recent superseded version (highest versionNumber among superseded).
  const superseded = versions.filter((v) => v.superseded);
  if (superseded.length === 0) return null;
  return superseded.reduce((a, b) => (a.versionNumber > b.versionNumber ? a : b)).id;
}

function defaultToVersionId(versions: BlueprintVersion[]): string | null {
  // Current (non-superseded) version. Falls back to highest-numbered.
  const current = versions.find((v) => !v.superseded);
  if (current) return current.id;
  if (versions.length === 0) return null;
  return versions.reduce((a, b) => (a.versionNumber > b.versionNumber ? a : b)).id;
}

export function BlueprintAuditPanel({ events, versions }: Props) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => a.versionNumber - b.versionNumber),
    [versions],
  );

  const [fromId, setFromId] = useState<string | null>(() => defaultFromVersionId(versions));
  const [toId, setToId] = useState<string | null>(() => defaultToVersionId(versions));

  const fromVersion = sortedVersions.find((v) => v.id === fromId) ?? null;
  const toVersion = sortedVersions.find((v) => v.id === toId) ?? null;
  const showCompare = sortedVersions.length >= 2;

  if (events.length === 0 && sortedVersions.length === 0) return null;

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={contentId}
        className="flex w-full items-center gap-2 text-left"
      >
        {expanded ? (
          <ChevronDown size={16} className="text-text-muted" />
        ) : (
          <ChevronRight size={16} className="text-text-muted" />
        )}
        <h3 className="text-sm font-medium text-text-secondary">
          Blueprint audit
          <span className="ml-1 text-text-faint">
            · {events.length} {events.length === 1 ? "event" : "events"}
          </span>
        </h3>
      </button>
      {expanded && (
        <div id={contentId} className="mt-3 space-y-4">
          <BlueprintLockHistory events={events} />
          {showCompare && (
            <div className="border-t border-border pt-3">
              <h4 className="mb-2 text-xs font-medium text-text-secondary">Compare versions</h4>
              <div className="mb-3 flex items-center gap-2 text-xs">
                <label htmlFor={`${contentId}-from`} className="text-text-muted">
                  From
                </label>
                <select
                  id={`${contentId}-from`}
                  value={fromId ?? ""}
                  onChange={(e) => setFromId(e.target.value)}
                  className="rounded border border-border bg-card px-2 py-1 text-xs"
                >
                  {sortedVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber}
                      {v.superseded ? " (superseded)" : ""}
                    </option>
                  ))}
                </select>
                <span className="text-text-faint">→</span>
                <label htmlFor={`${contentId}-to`} className="text-text-muted">
                  To
                </label>
                <select
                  id={`${contentId}-to`}
                  value={toId ?? ""}
                  onChange={(e) => setToId(e.target.value)}
                  className="rounded border border-border bg-card px-2 py-1 text-xs"
                >
                  {sortedVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber}
                      {v.superseded ? " (superseded)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              {fromVersion && toVersion && fromVersion.id !== toVersion.id ? (
                <BlueprintVersionDiff oldVersion={fromVersion} newVersion={toVersion} />
              ) : (
                <p className="text-xs text-text-faint">Pick two different versions to compare.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
